import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { API } from '@/App';
import { COMPARE_ENABLED } from '@/lib/featureFlags';
import { VIEW_SATISFINDER_PROFILES, hasPermission } from '@/lib/staffPermissions';
import { 
  LayoutDashboard, 
  Building2, 
  QrCode, 
  Users, 
  Settings, 
  LogOut, 
  Menu, 
  X,
  ChevronDown,
  MessageSquare,
  FileBarChart,
  AlertTriangle,
  Layers,
  ClipboardList,
  Crown,
  ArrowLeft,
  ShieldCheck,
  UserSearch,
  FileText,
  CreditCard,
  GitCompare,
  Globe,
  Network,
  PanelLeftClose,
  PanelLeftOpen
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import WelcomeBanner from '@/components/WelcomeBanner';
import useTenantFeatures from '@/hooks/useTenantFeatures';
import useResponsiveNav from '@/hooks/useResponsiveNav';
import { SubscriptionAccessBanner } from '@/components/subscription/SubscriptionPages';

const FOCUS_RING = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-offset-2 focus-visible:ring-offset-[#1A3E6F]';
const testId = (label) => `nav-${label.toLowerCase().replace(/\s+/g, '-')}`;

// Shows a right-side tooltip only while the desktop sidebar is collapsed.
function NavTip({ label, enabled, children }) {
  if (!enabled) return children;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="right" sideOffset={10}>{label}</TooltipContent>
    </Tooltip>
  );
}

function SidebarLink({ item, isActive, iconOnly, accent, activeBg, nav }) {
  return (
    <NavTip label={item.label} enabled={iconOnly}>
      <NavLink
        to={item.path}
        onClick={isActive ? nav.closeDrawer : nav.closeAfterNavigate}
        className={`flex items-center gap-3 min-h-[44px] rounded-lg text-sm font-medium transition-colors ${iconOnly ? 'justify-center px-0' : 'px-4 py-2.5'} ${FOCUS_RING}`}
        style={{
          color: isActive ? '#FFFFFF' : 'rgba(255,255,255,0.7)',
          backgroundColor: isActive ? activeBg : 'transparent',
          borderLeft: `3px solid ${isActive ? accent : 'transparent'}`,
        }}
        data-testid={testId(item.label)}
        data-active={isActive ? 'true' : 'false'}
      >
        <item.icon className="w-5 h-5 shrink-0" aria-hidden="true" />
        <span className={iconOnly ? 'sr-only' : 'truncate'}>{item.label}</span>
      </NavLink>
    </NavTip>
  );
}

export default function DashboardLayout({ children }) {
  const { user, logout, impersonating, exitImpersonation, hasGapLibrary, hasCases, hasValidationCodes } = useAuth();
  const tenantFeatures = useTenantFeatures();
  const location = useLocation();
  const nav = useResponsiveNav('vox.sidebar.collapsed', location.pathname);
  const iconOnly = nav.collapsed;
  const [clientLogo, setClientLogo] = useState(null);

  useEffect(() => {
    fetch(`${API}/tenant/branding`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.branding?.logo) setClientLogo(d.branding.logo); })
      .catch(() => {});
  }, []);

  const platformNavItems = [
    { path: '/platform', label: 'Platform Dashboard', icon: Crown, roles: ['platform_admin'] },
    { path: '/qa-queue', label: 'QA Queue', icon: ShieldCheck, roles: ['platform_admin', 'qa'] },
    { path: '/qa-admin', label: 'QA Reviewers', icon: Users, roles: ['platform_admin'] },
    { path: '/admin/satisfinders', label: 'satisFINDERs', icon: UserSearch, roles: ['platform_admin'], permission: VIEW_SATISFINDER_PROFILES },
    { path: '/admin/supported-countries', label: 'Supported Countries', icon: Globe, roles: ['platform_admin'] },
    { path: '/admin/pages', label: 'Content / Pages', icon: FileText, roles: ['platform_admin'] },
    { path: '/platform/taxonomy-templates', label: 'Taxonomy Templates', icon: Network, roles: ['platform_admin'] },
  ];

  // Tenant-level nav. `feature` on an item means it's only rendered when the
  // tenant has that feature flag enabled (Platform Admin settings panel).
  // Dashboard, Responses, Branches, Surveys, Users, Settings are always shown.
  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['tenant_admin', 'regional_manager', 'area_manager', 'branch_manager', 'hq_staff', 'platform_admin'] },
    { path: '/responses', label: 'Responses', icon: MessageSquare, roles: ['tenant_admin', 'regional_manager', 'area_manager', 'branch_manager', 'hq_staff', 'platform_admin'], surveyFeature: 'standard' },
    { path: '/reports', label: 'Reports', icon: FileBarChart, roles: ['tenant_admin', 'regional_manager', 'area_manager', 'branch_manager', 'hq_staff'], surveyFeature: 'mystery_shopping' },
    // Feb 2026 — Experience Performance. Same access-control envelope as
    // Reports (surveyFeature: mystery_shopping) so it inherits MS product
    // gating without introducing a new feature flag.
    { path: '/experience-performance', label: 'Experience Performance', icon: FileBarChart, roles: ['tenant_admin', 'regional_manager', 'area_manager', 'branch_manager', 'hq_staff'], surveyFeature: 'mystery_shopping' },
    { path: '/compare', label: 'Compare', icon: GitCompare, roles: ['tenant_admin', 'regional_manager', 'area_manager', 'branch_manager', 'hq_staff'], surveyFeature: 'mystery_shopping' },
    { path: '/cases', label: 'Cases', icon: AlertTriangle, roles: ['tenant_admin', 'regional_manager', 'area_manager', 'branch_manager', 'hq_staff', 'platform_admin'], feature: 'cases' },
    { path: '/branches', label: 'Branches', icon: Building2, roles: ['tenant_admin', 'platform_admin'] },
    { path: '/codes', label: 'Validation Codes', icon: QrCode, roles: ['tenant_admin', 'platform_admin'], feature: 'validation_codes' },
    { path: '/surveys', label: 'Surveys', icon: ClipboardList, roles: ['tenant_admin', 'platform_admin'] },
    { path: '/gaps', label: 'Gap Library', icon: Layers, roles: ['tenant_admin', 'platform_admin'], feature: 'gap_library' },
    { path: '/taxonomy', label: 'Attributes & Factors', icon: Network, roles: ['tenant_admin', 'super_admin', 'platform_admin'], surveyFeature: 'standard' },
    { path: '/users', label: 'Users', icon: Users, roles: ['tenant_admin', 'platform_admin'] },
    { path: '/admin/subscription', label: 'Subscription', icon: CreditCard, roles: ['tenant_admin'] },
    { path: '/settings', label: 'Settings', icon: Settings, roles: ['tenant_admin', 'platform_admin'] },
  ];

  const isPlatformAdmin = user?.role === 'platform_admin';
  const isImpersonating = isPlatformAdmin && impersonating?.impersonating;

  const isFeatureEnabled = (key) => {
    if (!key) return true;
    if (key === 'cases') return hasCases;
    if (key === 'validation_codes') return hasValidationCodes;
    if (key === 'gap_library') return hasGapLibrary;
    return false;
  };

  // Fix 2 — Survey-type gating for /responses and /reports.
  // Platform admin (when NOT impersonating) bypasses; the impersonated
  // tenant's features still drive what shows under impersonation.
  // Until the features call resolves we keep the legacy behaviour (show both)
  // by treating `_loaded === false` as "show everything" to avoid a flash
  // where a tenant's working sidebar disappears.
  const isSurveyFeatureEnabled = (kind) => {
    if (!kind) return true;
    if (user?.role === 'platform_admin' && !isImpersonating) return true;
    if (!tenantFeatures._loaded) return true;  // pre-resolution: don't hide
    if (kind === 'standard') return !!tenantFeatures.has_standard_surveys;
    if (kind === 'mystery_shopping') return !!tenantFeatures.has_mystery_shopping;
    return true;
  };

  // Platform admin without impersonation only sees platform nav.
  // Platform admin with impersonation sees platform nav + the gated tenant nav.
  const filteredPlatformNav = platformNavItems.filter(item =>
    item.roles.includes(user?.role || '') || (item.permission && hasPermission(user, item.permission)));
  const filteredNavItems = (!isPlatformAdmin || isImpersonating)
    ? navItems.filter(item =>
        item.roles.includes(user?.role || 'branch_manager')
        && (item.path !== '/compare' || COMPARE_ENABLED)  // temporarily hidden
        && isFeatureEnabled(item.feature)
        && isSurveyFeatureEnabled(item.surveyFeature))
    : [];

  const getRoleBadge = (role) => {
    const badges = {
      platform_admin: { label: 'Platform', color: 'bg-[#7C3AED] text-white' },
      qa: { label: 'QA', color: 'bg-[#0EA5E9] text-white' },
      client: { label: 'Client', color: 'bg-[#6366F1] text-white' },
      tenant_admin: { label: 'Admin', color: 'bg-[#F1D511] text-[#0A0F1B]' },
      regional_manager: { label: 'Regional', color: 'bg-[#5BC4BF] text-white' },
      branch_manager: { label: 'Branch', color: 'bg-[#4EBC87] text-white' }
    };
    return badges[role] || badges.branch_manager;
  };

  const roleBadge = getRoleBadge(user?.role);

  return (
    <TooltipProvider delayDuration={150}>
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Mobile / tablet header */}
      <header
        className="lg:hidden fixed top-0 left-0 right-0 z-40 h-16 bg-white border-b border-[#E2E8F0] px-3 sm:px-4 flex items-center justify-between gap-2"
        data-testid="mobile-header"
      >
        <button
          ref={nav.menuButtonRef}
          type="button"
          onClick={nav.openDrawer}
          aria-label="Open menu"
          aria-expanded={nav.drawerOpen}
          aria-controls="dashboard-sidebar"
          className="inline-flex items-center gap-2 h-11 px-3 rounded-lg border border-[#E2E8F0] text-[#0F172A] text-sm font-semibold hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1A3E6F] focus-visible:ring-offset-2"
          data-testid="mobile-menu-btn"
        >
          <Menu className="w-5 h-5" aria-hidden="true" />
          <span>Menu</span>
        </button>
        <div className="flex items-center gap-2 min-w-0">
          {clientLogo ? (
            <img src={clientLogo} alt="Logo" className="h-8 max-w-[96px] object-contain rounded" />
          ) : (
            <img
              src="/satisfind-logo.png"
              alt="satisFIND"
              className="h-8"
              data-testid="dashboard-default-logo-mobile"
            />
          )}
          <span className="font-['Montserrat'] font-bold text-[#0F172A]">VOX</span>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="Account menu"
              className="h-11 w-11 inline-flex items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1A3E6F]"
              data-testid="mobile-account-btn"
            >
              <Avatar className="w-8 h-8">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="bg-[#1A3E6F] text-white text-sm">
                  {user?.name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={logout} className="text-red-600" data-testid="mobile-logout">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      {/* Drawer backdrop (mobile / tablet) */}
      <div
        className={`lg:hidden fixed inset-0 z-40 bg-black/50 transition-opacity duration-300 motion-reduce:transition-none ${nav.drawerOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={nav.closeDrawer}
        aria-hidden="true"
        data-testid="sidebar-overlay"
      />

      {/* Sidebar: drawer below 1024px, collapsible rail on desktop */}
      <aside
        id="dashboard-sidebar"
        ref={nav.drawerRef}
        inert={nav.navHidden}
        role={nav.drawerOpen ? 'dialog' : undefined}
        aria-modal={nav.drawerOpen ? 'true' : undefined}
        aria-label="Main navigation"
        style={{ backgroundColor: '#1A3E6F' }}
        className={`fixed left-0 top-0 z-50 h-[100dvh] flex flex-col w-[280px] max-w-[85vw] transition-[transform,width,visibility] duration-300 motion-reduce:transition-none ${nav.drawerOpen ? 'translate-x-0 visible shadow-2xl' : '-translate-x-full invisible'} lg:max-w-none lg:translate-x-0 lg:visible lg:shadow-none ${iconOnly ? 'lg:w-[76px]' : 'lg:w-[260px]'}`}
        data-testid="dashboard-sidebar"
        data-collapsed={iconOnly ? 'true' : 'false'}
      >
        {/* Logo + controls */}
        <div className={`shrink-0 flex items-start gap-2 pt-5 pb-4 ${iconOnly ? 'justify-center px-2' : 'justify-between pl-6 pr-3'}`}>
          {!iconOnly && (
            <div className="flex flex-col items-start gap-2 min-w-0">
              {clientLogo ? (
                <img src={clientLogo} alt="Logo" className="h-10 max-w-full object-contain rounded-lg" />
              ) : (
                <img
                  src="https://customer-assets.emergentagent.com/job_restaurant-voice/artifacts/7w3grvpa_satisFIND_20th_Logo_white_transparent.png"
                  alt="satisFIND"
                  style={{ width: 160, maxWidth: '100%', height: 'auto' }}
                  data-testid="dashboard-default-logo"
                />
              )}
              <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.4)' }}>powered by satisFIND&reg;</span>
            </div>
          )}
          <button
            type="button"
            onClick={nav.closeDrawer}
            aria-label="Close menu"
            className={`lg:hidden shrink-0 h-11 w-11 inline-flex items-center justify-center rounded-lg text-white hover:bg-white/10 ${FOCUS_RING}`}
            data-testid="sidebar-close-btn"
          >
            <X className="w-6 h-6" aria-hidden="true" />
          </button>
          <NavTip label="Expand sidebar" enabled={iconOnly}>
            <button
              type="button"
              onClick={nav.toggleCollapsed}
              aria-label={iconOnly ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-expanded={!iconOnly}
              aria-controls="dashboard-sidebar"
              title={iconOnly ? undefined : 'Collapse sidebar'}
              className={`hidden lg:inline-flex shrink-0 h-11 w-11 items-center justify-center rounded-lg text-white/80 hover:text-white hover:bg-white/10 ${FOCUS_RING}`}
              data-testid="sidebar-collapse-btn"
            >
              {iconOnly ? <PanelLeftOpen className="w-5 h-5" aria-hidden="true" /> : <PanelLeftClose className="w-5 h-5" aria-hidden="true" />}
            </button>
          </NavTip>
        </div>

        {/* Nav (scrolls independently) */}
        <div className={`flex-1 min-h-0 overflow-y-auto overscroll-contain pb-4 ${iconOnly ? 'px-2' : 'px-4'}`} data-testid="sidebar-scroll">
          <nav className="space-y-1" aria-label="Primary">
            {/* Platform nav (platform_admin only) */}
            {filteredPlatformNav.map((item) => (
              <SidebarLink
                key={item.path}
                item={item}
                isActive={location.pathname === item.path}
                iconOnly={iconOnly}
                accent="#A78BFA"
                activeBg="rgba(124,58,237,0.3)"
                nav={nav}
              />
            ))}

            {/* Impersonation banner */}
            {isImpersonating && (iconOnly ? (
              <NavTip label={`Viewing as ${impersonating.tenant_name} — Back to Platform`} enabled>
                <button
                  type="button"
                  onClick={() => { exitImpersonation(); nav.closeDrawer(); }}
                  aria-label={`Viewing as ${impersonating.tenant_name}. Back to Platform`}
                  className={`w-full min-h-[44px] my-2 inline-flex items-center justify-center rounded-lg ${FOCUS_RING}`}
                  style={{ backgroundColor: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.3)', color: '#FCD34D' }}
                  data-testid="exit-impersonation-btn"
                >
                  <ArrowLeft className="w-5 h-5" aria-hidden="true" />
                </button>
              </NavTip>
            ) : (
              <div className="mx-2 my-3 p-3 rounded-lg" style={{ backgroundColor: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.3)' }}>
                <p className="text-[11px] font-medium" style={{ color: '#FCD34D' }}>Viewing as</p>
                <p className="text-sm font-semibold text-white truncate">{impersonating.tenant_name}</p>
                <button
                  type="button"
                  onClick={() => { exitImpersonation(); nav.closeDrawer(); }}
                  className={`flex items-center gap-1.5 mt-2 min-h-[32px] text-xs font-medium hover:underline rounded ${FOCUS_RING}`}
                  style={{ color: '#FCD34D' }}
                  data-testid="exit-impersonation-btn"
                >
                  <ArrowLeft className="w-3.5 h-3.5" /> Back to Platform
                </button>
              </div>
            ))}

            {/* Tenant section separator */}
            {isPlatformAdmin && isImpersonating && filteredNavItems.length > 0 && (iconOnly ? (
              <div className="mx-2 my-2" style={{ borderTop: '1px solid rgba(255,255,255,0.15)' }} aria-hidden="true" />
            ) : (
              <div className="px-4 pt-2 pb-1">
                <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.35)' }}>Tenant</p>
              </div>
            ))}

            {/* Tenant nav items */}
            {filteredNavItems.map((item) => (
              <SidebarLink
                key={item.path}
                item={item}
                isActive={location.pathname === item.path}
                iconOnly={iconOnly}
                accent="#F1D511"
                activeBg="rgba(255,255,255,0.1)"
                nav={nav}
              />
            ))}
          </nav>
        </div>

        {/* User Info at bottom (always visible) */}
        <div className={`shrink-0 ${iconOnly ? 'p-2' : 'p-4'}`} style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <DropdownMenu>
            <NavTip label={`${user?.name || 'Account'} — account & logout`} enabled={iconOnly}>
              <DropdownMenuTrigger asChild>
                <button 
                  type="button"
                  aria-label={iconOnly ? `Account menu for ${user?.name || 'user'}` : undefined}
                  className={`w-full flex items-center gap-3 rounded-lg transition-colors hover:bg-white/10 ${iconOnly ? 'justify-center p-2' : 'p-3'} ${FOCUS_RING}`}
                  style={{ color: 'white' }}
                  data-testid="user-menu-btn"
                >
                  <Avatar className="w-10 h-10">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}>
                      {user?.name?.charAt(0) || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  {!iconOnly && (
                    <>
                      <div className="flex-1 text-left min-w-0">
                        <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${roleBadge.color}`}>
                          {roleBadge.label}
                        </span>
                      </div>
                      <ChevronDown className="w-4 h-4" style={{ color: 'rgba(255,255,255,0.5)' }} />
                    </>
                  )}
                </button>
              </DropdownMenuTrigger>
            </NavTip>
            <DropdownMenuContent align="end" side={iconOnly ? 'right' : 'top'} className="w-56">
              <div className="px-3 py-2">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-[#64748B] truncate">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => window.location.href = '/settings'}>
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-red-600" data-testid="sidebar-logout">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main Content */}
      <main
        ref={nav.mainRef}
        id="main-content"
        className={`min-h-screen min-w-0 px-4 pb-6 pt-[5.5rem] sm:px-6 lg:pt-6 transition-[margin] duration-300 motion-reduce:transition-none focus:outline-none ${iconOnly ? 'lg:ml-[76px]' : 'lg:ml-[260px]'}`}
        data-testid="dashboard-main"
      >
        <WelcomeBanner />
        <SubscriptionAccessBanner />
        {children}
      </main>
    </div>
    </TooltipProvider>
  );
}
